import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';

export interface NcGalleriaImage {
  src: string;
  title?: string;
}

@Component({
  selector: 'nc-galleria',
  templateUrl: './galleria.component.html',
  styleUrls: ['./galleria.component.less']
})
export class GalleriaComponent implements OnInit, OnDestroy, AfterViewInit {
  /** 图片数组 */
  private _images: NcGalleriaImage[] = [];
  @Input()
  set ncImages(value: string[] | NcGalleriaImage[]) {
    // 如果传入的是string[]，转换为NcGalleriaImage[]
    if (value && value.length > 0 && typeof value[0] === 'string') {
      this._images = (value as string[]).map(src => ({
        src: src,
        title: ''
      }));
    } else {
      this._images = value as NcGalleriaImage[];
    }
    setTimeout(() => {
      this.updateActualContainerWidth();
      this.init();
    });
  }
  get ncImages(): NcGalleriaImage[] {
    return this._images;
  }
  /** 组件宽度 */
  @Input() ncWidth: string = '100%';
  /** 组件高度 */
  @Input() ncHeight: string = '100%';
  /** 缩略图宽度 */
  @Input() ncThumbnailWidth: number = 80;
  /** 缩略图高度 */
  @Input() ncThumbnailHeight: number = 60;
  /** 图片适应方式 */
  @Input() ncObjectFit: 'contain' | 'cover' | 'fill' = 'contain';
  /** 自动播放 */
  private _autoPlay: boolean = false;
  @Input()
  set ncAutoPlay(val: boolean | string) {
    this._autoPlay = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncAutoPlay(): boolean {
    return this._autoPlay;
  }
  /** 自动播放间隔 */
  @Input() ncAutoPlayInterval: number = 3000;
  /** 显示缩略图 */
  private _showThumbnails: boolean = true;
  @Input()
  set ncShowThumbnails(val: boolean | string) {
    this._showThumbnails = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncShowThumbnails(): boolean {
    return this._showThumbnails;
  }
  /** 显示指示器 */
  private _showIndicators: boolean = false;
  @Input()
  set ncShowIndicators(val: boolean | string) {
    this._showIndicators = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncShowIndicators(): boolean {
    return this._showIndicators;
  }
  /** 指示器显示位置 */
  @Input() ncIndicatorsPosition: 'inside' | 'outside' = 'outside';
  /** 显示导航按钮 */
  private _showNavButtons: boolean = false;
  @Input()
  set ncShowNavButtons(val: boolean | string) {
    this._showNavButtons = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncShowNavButtons(): boolean {
    return this._showNavButtons;
  }
  /** 导航按钮显示方式 */
  @Input() ncNavButtonsDisplay: 'hover' | 'always' = 'always';
  /** 启用全屏预览 */
  private _enableFullscreen: boolean = false;
  @Input()
  set ncEnableFullscreen(val: boolean | string) {
    this._enableFullscreen = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncEnableFullscreen(): boolean {
    return this._enableFullscreen;
  }
  /** 全屏按钮显示方式 */
  @Input() ncFullscreenButtonDisplay: 'hover' | 'always' | 'hidden' = 'always';
  /** 显示图片数目 */
  private _showIndex: boolean = false;
  @Input()
  set ncShowIndex(val: boolean | string) {
    this._showIndex = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncShowIndex(): boolean {
    return this._showIndex;
  }
  /** 显示图片标题 */
  private _showTitle: boolean = false;
  @Input()
  set ncShowTitle(val: boolean | string) {
    this._showTitle = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncShowTitle(): boolean {
    return this._showTitle;
  }
  /** 滚轮缩放 */
  private _wheelScale: boolean = false;
  @Input()
  set ncWheelScale(val: boolean | string) {
    this._wheelScale = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncWheelScale(): boolean {
    return this._wheelScale;
  }

  currentIndex: number = 0;
  thumbnailStartIndex: number = 0;
  thumbnailTransform: number = 0; // 缩略图滚动偏移量
  isFullscreen: boolean = false; // 全屏状态
  isFullscreenAutoPlaying: boolean = false; // 全屏模式下的自动播放状态
  private autoPlayTimer: any;

  // 全屏缩放相关属性
  fullscreenScale: number = 1; // 当前缩放比例
  fullscreenTranslateX: number = 0; // X轴偏移
  fullscreenTranslateY: number = 0; // Y轴偏移
  private minScale: number = 0.5; // 最小缩放比例
  private maxScale: number = 3; // 最大缩放比例
  private isDragging: boolean = false; // 是否正在拖拽
  private lastMouseX: number = 0; // 上次鼠标X位置
  private lastMouseY: number = 0; // 上次鼠标Y位置

  @ViewChild('galleriaContainer', { static: false }) galleriaContainer!: ElementRef;
  private actualContainerWidth: number = 0;
  private resizeTimeout: any;

  // 计算缩略图实际宽度（包含gap）
  get thumbnailActualWidth(): number {
    return this.ncThumbnailWidth + 8; // 8px gap
  }

  // 计算实际可显示的缩略图数量
  get actualVisibleThumbnailCount(): number {
    // 边界保护：如果没有图片，返回0
    if (!this.ncImages || this.ncImages.length === 0) {
      return 0;
    }

    let containerWidth = 0;

    if (this.ncWidth && this.ncWidth.includes('px')) {
      // 如果设置了具体像素宽度
      containerWidth = parseInt(this.ncWidth.replace('px', ''));
    } else if (this.actualContainerWidth > 0) {
      // 如果是百分比宽度，使用实际测量的宽度
      containerWidth = this.actualContainerWidth;
    } else {
      // 如果还没有测量到实际宽度，先返回图片数量（避免初始化时的问题）
      return this.ncImages.length;
    }

    // 边界保护：确保容器宽度和缩略图宽度都是正数
    if (containerWidth <= 0 || this.thumbnailActualWidth <= 0) {
      return 1;
    }

    // 减去左右滚动按钮的宽度（32px + 8px gap）× 2 = 80px，再减去容器padding
    const availableWidth = containerWidth - 80 - 32; // 额外减去32px的padding

    // 边界保护：确保可用宽度为正数
    if (availableWidth <= 0) {
      return 1;
    }

    const visibleCount = Math.floor(availableWidth / this.thumbnailActualWidth);
    // 确保至少显示1个，最多不超过图片总数
    return Math.max(1, Math.min(visibleCount, this.ncImages.length));
  }

  // 判断缩略图是否应该居中显示
  get shouldCenterThumbnails(): boolean {
    return this.ncImages.length <= this.actualVisibleThumbnailCount;
  }

  // 计算缩略图最大滚动距离
  private getMaxThumbnailScroll(): number {
    const actualVisibleCount = this.actualVisibleThumbnailCount;

    if (this.ncImages.length <= actualVisibleCount) {
      return 0;
    }

    // 计算总宽度减去实际可见区域宽度
    const totalWidth = this.ncImages.length * this.thumbnailActualWidth;
    const visibleWidth = actualVisibleCount * this.thumbnailActualWidth;
    return -(totalWidth - visibleWidth);
  }

  constructor() { }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {
    // 获取实际的容器宽度
    this.updateActualContainerWidth();
  }

  private updateActualContainerWidth(): void {
    if (this.galleriaContainer && this.galleriaContainer.nativeElement) {
      const newWidth = this.galleriaContainer.nativeElement.offsetWidth;
      // 只有当宽度真正发生变化时才更新
      if (newWidth !== this.actualContainerWidth) {
        const oldVisibleCount = this.actualVisibleThumbnailCount;
        this.actualContainerWidth = newWidth;
        const newVisibleCount = this.actualVisibleThumbnailCount;

        // 如果可显示数量发生变化，重新调整缩略图视图
        if (oldVisibleCount !== newVisibleCount) {
          this.updateThumbnailView();
        }
      }
    }
  }

  init(): void {
    //先删除autoPlayTimer定时器，防止ncImages更换时重复autoPlayTimer
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
    if (this.ncAutoPlay && this.ncImages.length > 1) {
      this.startAutoPlay();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
    // 清理resize定时器
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    // 如果组件销毁时还在全屏状态，恢复页面滚动
    if (this.isFullscreen) {
      document.body.style.overflow = '';
    }
  }

  // 选择图片
  selectImage(index: number): void {
    if (index >= 0 && index < this.ncImages.length) {
      this.currentIndex = index;
      this.updateThumbnailView();
      // 切换图片时重置缩放状态
      this.resetFullscreenZoom();
    }
  }

  // 重置全屏缩放状态
  private resetFullscreenZoom(): void {
    this.fullscreenScale = 1;
    this.fullscreenTranslateX = 0;
    this.fullscreenTranslateY = 0;
  }

  // 上一张图片
  previousImage(): void {
    this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.ncImages.length - 1;
    this.updateThumbnailView();
    // 切换图片时重置缩放状态
    this.resetFullscreenZoom();
  }

  // 下一张图片
  nextImage(): void {
    this.currentIndex = this.currentIndex < this.ncImages.length - 1 ? this.currentIndex + 1 : 0;
    this.updateThumbnailView();
    // 切换图片时重置缩放状态
    this.resetFullscreenZoom();
  }

  // 更新缩略图视图
  private updateThumbnailView(): void {
    const actualVisibleCount = this.actualVisibleThumbnailCount;

    if (this.ncImages.length <= actualVisibleCount) {
      this.thumbnailStartIndex = 0;
      this.thumbnailTransform = 0;
      return;
    }

    // 根据缩略图数量决定滚动策略
    const triggerIndex = Math.max(2, actualVisibleCount - 2);

    if (this.currentIndex < triggerIndex) {
      // 前面几张图片时，缩略图位置不变
      this.thumbnailStartIndex = 0;
    } else if (this.currentIndex >= this.ncImages.length - Math.floor(actualVisibleCount / 2)) {
      // 最后几张图片时，显示最后几张缩略图
      this.thumbnailStartIndex = this.ncImages.length - actualVisibleCount;
    } else {
      // 中间部分，让当前图片居中或接近居中显示
      const centerOffset = Math.floor(actualVisibleCount / 2);
      this.thumbnailStartIndex = this.currentIndex - centerOffset;
    }

    // 确保不超出边界
    this.thumbnailStartIndex = Math.max(0, Math.min(this.thumbnailStartIndex, this.ncImages.length - actualVisibleCount));

    // 计算transform偏移量
    this.thumbnailTransform = -this.thumbnailStartIndex * this.thumbnailActualWidth;

    // 确保不超出最大滚动范围
    const maxScroll = this.getMaxThumbnailScroll();
    this.thumbnailTransform = Math.max(maxScroll, this.thumbnailTransform);
  }

  // 缩略图向左滚动
  scrollThumbnailsLeft(): void {
    if (this.canScrollLeft()) {
      // 每次滚动一个缩略图的宽度
      this.thumbnailTransform += this.thumbnailActualWidth;
      // 确保不超出左边界
      this.thumbnailTransform = Math.min(0, this.thumbnailTransform);
      // 更新thumbnailStartIndex以保持一致性
      this.thumbnailStartIndex = Math.max(0, Math.round(-this.thumbnailTransform / this.thumbnailActualWidth));
    }
  }

  // 缩略图向右滚动
  scrollThumbnailsRight(): void {
    if (this.canScrollRight()) {
      // 每次滚动一个缩略图的宽度
      this.thumbnailTransform -= this.thumbnailActualWidth;
      // 确保不超出右边界
      const maxScroll = this.getMaxThumbnailScroll();
      this.thumbnailTransform = Math.max(maxScroll, this.thumbnailTransform);
      // 更新thumbnailStartIndex以保持一致性
      this.thumbnailStartIndex = Math.max(0, Math.round(-this.thumbnailTransform / this.thumbnailActualWidth));
    }
  }

  // 是否可以向左滚动缩略图
  canScrollLeft(): boolean {
    return this.thumbnailTransform < 0;
  }

  // 是否可以向右滚动缩略图
  canScrollRight(): boolean {
    if (this.ncImages.length <= this.actualVisibleThumbnailCount) {
      return false;
    }
    const maxScroll = this.getMaxThumbnailScroll();
    return this.thumbnailTransform > maxScroll;
  }

  // 开始自动播放
  private startAutoPlay(): void {
    this.autoPlayTimer = setInterval(() => {
      this.nextImage();
    }, this.ncAutoPlayInterval);
  }

  // 停止自动播放
  private stopAutoPlay(): void {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  // 鼠标进入时暂停自动播放
  onMouseEnter(): void {
    // 如果在全屏模式下，不要自动暂停播放，让用户手动控制
    if (this.isFullscreen) {
      return;
    }

    if (this.ncAutoPlay) {
      this.stopAutoPlay();
    }
  }

  // 鼠标离开时恢复自动播放
  onMouseLeave(): void {
    // 如果在全屏模式下，不要自动恢复播放，让用户手动控制
    if (this.isFullscreen) {
      return;
    }

    if (this.ncAutoPlay && this.ncImages.length > 1) {
      this.startAutoPlay();
    }
  }

  // 监听窗口大小变化
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    // 使用防抖处理，避免频繁计算
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.updateActualContainerWidth();
    }, 150); // 150ms防抖延迟
  }

  // 键盘导航支持
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.ncImages || this.ncImages.length <= 1) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextImage();
        break;
      case 'Home':
        event.preventDefault();
        this.selectImage(0);
        break;
      case 'End':
        event.preventDefault();
        this.selectImage(this.ncImages.length - 1);
        break;
    }
  }

  // 图片加载错误处理
  onImageError(event: any): void {
    // 防止错误图片再次触发错误事件
    if (event.target.src.startsWith('data:image/svg+xml')) {
      return;
    }

    console.warn('图片加载失败:', event.target.src);
    // 使用SVG占位图
    const errorSvg = this.createErrorImageSvg(400, 300);
    event.target.src = errorSvg;
    event.target.style.objectFit = 'contain';
  }

  // 缩略图加载错误处理
  onThumbnailError(event: any): void {
    // 防止错误图片再次触发错误事件
    if (event.target.src.startsWith('data:image/svg+xml')) {
      return;
    }

    console.warn('缩略图加载失败:', event.target.src);
    // 使用SVG占位图
    const errorSvg = this.createErrorImageSvg(this.ncThumbnailWidth, this.ncThumbnailHeight);
    event.target.src = errorSvg;
    event.target.style.objectFit = 'contain';
  }

  // 创建错误图片的SVG占位图
  private createErrorImageSvg(width: number, height: number): string {
    const svg = `
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f8f9fa" stroke="#dee2e6" stroke-width="2"/>
        <g transform="translate(${width / 2}, ${height / 2})">
          <!-- 破损图片图标 -->
          <g transform="translate(-24, -24)">
            <rect x="4" y="4" width="40" height="32" rx="2" fill="none" stroke="#6c757d" stroke-width="2"/>
            <circle cx="14" cy="14" r="2" fill="#6c757d"/>
            <path d="M44 28L36 20L24 32L16 24L4 36" stroke="#6c757d" stroke-width="2" fill="none"/>
            <!-- X 标记 -->
            <line x1="32" y1="8" x2="40" y2="16" stroke="#dc3545" stroke-width="2"/>
            <line x1="40" y1="8" x2="32" y2="16" stroke="#dc3545" stroke-width="2"/>
          </g>
          <!-- 错误文字 -->
          <text x="0" y="32" text-anchor="middle" fill="#6c757d" font-family="Arial, sans-serif" font-size="12">
            图片加载失败
          </text>
        </g>
      </svg>
    `;

    // 使用现代方法：直接URL编码，无需base64
    return 'data:image/svg+xml,' + encodeURIComponent(svg);
  }

  // 打开全屏预览
  openFullscreen(): void {
    if (this.ncEnableFullscreen) {
      this.isFullscreen = true;
      // 阻止页面滚动
      document.body.style.overflow = 'hidden';
      // 重置缩放状态
      this.resetFullscreenZoom();

      // 检查当前是否有自动播放在运行
      const wasAutoPlaying = !!this.autoPlayTimer;
      // console.log('打开全屏，当前自动播放状态:', wasAutoPlaying);

      // 如果原本有自动播放，在全屏模式下继续播放
      if (this.ncAutoPlay && this.ncImages.length > 1) {
        this.isFullscreenAutoPlaying = wasAutoPlaying;
        // 如果之前没有在播放，需要启动
        if (!wasAutoPlaying) {
          this.startAutoPlay();
          this.isFullscreenAutoPlaying = true;
        }
      }
    }
  }

  // 关闭全屏预览
  closeFullscreen(): void {
    this.isFullscreen = false;
    this.isFullscreenAutoPlaying = false;
    // 恢复页面滚动
    document.body.style.overflow = '';
    // 重置缩放状态
    this.resetFullscreenZoom();
    //先删除autoPlayTimer定时器，防止恢复自动播放时重复autoPlayTimer
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
    // 退出全屏时恢复自动播放
    if (this.ncAutoPlay && this.ncImages.length > 1) {
      this.startAutoPlay();
    }
  }

  // 全屏模式下切换自动播放
  toggleFullscreenAutoPlay(): void {
    if (!this.isFullscreen || this.ncImages.length <= 1) {
      return;
    }

    // console.log('切换前状态:', this.isFullscreenAutoPlaying, '定时器:', !!this.autoPlayTimer);

    if (this.isFullscreenAutoPlaying) {
      this.stopAutoPlay();
      this.isFullscreenAutoPlaying = false;
      // console.log('已暂停自动播放');
    } else {
      this.startAutoPlay();
      this.isFullscreenAutoPlaying = true;
      // console.log('已开始自动播放');
    }
  }

  // 全屏模式下的键盘事件处理
  onFullscreenKeyDown(event: KeyboardEvent): void {
    if (!this.isFullscreen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeFullscreen();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextImage();
        break;
      case ' ': // 空格键
        event.preventDefault();
        if (this.ncAutoPlay) {
          this.toggleFullscreenAutoPlay();
        }
        break;
    }
  }

  // 全屏模式下的滚轮缩放事件
  onFullscreenWheel(event: WheelEvent): void {
    if (!this.isFullscreen || !this.ncWheelScale) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const delta = event.deltaY;
    const scaleStep = 0.1;
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    // 计算鼠标相对于图片的位置
    const mouseX = event.clientX - rect.left - rect.width / 2;
    const mouseY = event.clientY - rect.top - rect.height / 2;

    const oldScale = this.fullscreenScale;

    // 计算新的缩放比例
    if (delta < 0) {
      // 向上滚动，放大
      this.fullscreenScale = Math.min(this.maxScale, this.fullscreenScale + scaleStep);
    } else {
      // 向下滚动，缩小
      this.fullscreenScale = Math.max(this.minScale, this.fullscreenScale - scaleStep);
    }

    // 如果缩放比例发生了变化，调整偏移量以保持鼠标位置不变
    if (this.fullscreenScale !== oldScale) {
      const scaleRatio = this.fullscreenScale / oldScale;

      // 调整偏移量，使缩放以鼠标位置为中心
      this.fullscreenTranslateX = mouseX + (this.fullscreenTranslateX - mouseX) * scaleRatio;
      this.fullscreenTranslateY = mouseY + (this.fullscreenTranslateY - mouseY) * scaleRatio;

      // 限制偏移量范围
      this.constrainTranslation();
    }
  }

  // 全屏模式下的鼠标按下事件（开始拖拽）
  onFullscreenMouseDown(event: MouseEvent): void {
    if (!this.isFullscreen || !this.ncWheelScale || this.fullscreenScale <= 1) {
      return;
    }

    event.preventDefault();
    this.isDragging = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;

    // 改变鼠标样式
    document.body.style.cursor = 'grabbing';
  }

  // 全屏模式下的鼠标移动事件（拖拽中）
  onFullscreenMouseMove(event: MouseEvent): void {
    if (!this.isFullscreen || !this.ncWheelScale || !this.isDragging) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - this.lastMouseX;
    const deltaY = event.clientY - this.lastMouseY;

    this.fullscreenTranslateX += deltaX;
    this.fullscreenTranslateY += deltaY;

    // 限制偏移量范围
    this.constrainTranslation();

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  // 全屏模式下的鼠标松开事件（结束拖拽）
  onFullscreenMouseUp(event: MouseEvent): void {
    if (!this.isFullscreen || !this.ncWheelScale) {
      return;
    }

    this.isDragging = false;
    document.body.style.cursor = '';
  }

  // 限制图片偏移量范围
  private constrainTranslation(): void {
    if (this.fullscreenScale <= 1) {
      this.fullscreenTranslateX = 0;
      this.fullscreenTranslateY = 0;
      return;
    }

    // 获取全屏图片的实际显示尺寸（考虑object-fit: contain）
    const viewportWidth = window.innerWidth * 0.9; // 90vw
    const viewportHeight = window.innerHeight * 0.9; // 90vh

    // 计算图片在当前缩放下超出视口的部分
    const scaledWidth = viewportWidth * this.fullscreenScale;
    const scaledHeight = viewportHeight * this.fullscreenScale;

    const maxTranslateX = Math.max(0, (scaledWidth - window.innerWidth) / 2);
    const maxTranslateY = Math.max(0, (scaledHeight - window.innerHeight) / 2);

    // 限制X轴偏移
    this.fullscreenTranslateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.fullscreenTranslateX));
    // 限制Y轴偏移
    this.fullscreenTranslateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.fullscreenTranslateY));
  }

  // 获取全屏图片的transform样式
  getFullscreenImageTransform(): string {
    // 先平移再缩放，这样平移的像素值不会被缩放影响
    return `translate(${this.fullscreenTranslateX}px, ${this.fullscreenTranslateY}px) scale(${this.fullscreenScale})`;
  }

  // 双击重置缩放
  onFullscreenDoubleClick(event: MouseEvent): void {
    if (!this.isFullscreen || !this.ncWheelScale) {
      return;
    }

    event.preventDefault();
    this.resetFullscreenZoom();
  }
}
